import React, { useEffect, useMemo, useState } from "react";
import { Alert, Col, Form, Row, Spinner } from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BsArrowLeft, BsPlus, BsPrinter, BsSave, BsTrash } from "react-icons/bs";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../context/userAuth/UserAuthState";
import {
  createInvoiceAPI,
  getInvoiceAPI,
  nextInvoiceNoAPI,
  printInvoiceAPI,
  updateInvoiceAPI,
} from "../api/api";

const PAYMENT_STATUSES = [
  { value: "quotation", label: "Quotation" },
  { value: "unpaid", label: "Unpaid" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "cancelled", label: "Cancelled" },
];

const emptyLine = () => ({
  description: "",
  qty: 1,
  rate_pkr: 0,
  total_pkr: 0,
});

const defaultForm = {
  document_type: "invoice",
  payment_status: "unpaid",
  invoice_no: "",
  invoice_date: new Date().toISOString().slice(0, 10),
  buyer_name: "",
  buyer_company: "",
  buyer_address: "",
  ntn: "N/A",
  po_number: "N/A",
  po_date: "",
  test_method: "Vehicle Inspection & Certification",
  outstanding_note: "",
  outstanding_pkr: 0,
  line_items: [emptyLine()],
};

const money = (n) =>
  Number(n || 0).toLocaleString("en-PK", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const CreateInvoicePage = () => {
  const { user } = useAuth();
  const token = user?.authorization;
  const role = user?.user?.is_user;
  const isSuperAdmin = role === "isAdmin" || role === "super_admin" || role === "pro_super_admin";
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("id");

  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [savedId, setSavedId] = useState(editId ? Number(editId) : null);
  const [invoiceNoTouched, setInvoiceNoTouched] = useState(Boolean(editId));

  const applySuggestedNo = async (invoiceDate, buyerCompany) => {
    const res = await nextInvoiceNoAPI({
      invoice_date: invoiceDate,
      buyer_company: buyerCompany,
    }, token);
    if (res.success && res.invoice_no) {
      setForm((prev) => ({ ...prev, invoice_no: res.invoice_no }));
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      if (editId) {
        const res = await getInvoiceAPI({ id: editId }, token);
        if (res.success) {
          const inv = res.invoice;
          setSavedId(inv.id);
          setInvoiceNoTouched(true);
          setForm({
            document_type: inv.document_type || "invoice",
            payment_status: inv.payment_status || "unpaid",
            invoice_no: inv.invoice_no || "",
            invoice_date: inv.invoice_date || "",
            buyer_name: inv.buyer_name || "",
            buyer_company: inv.buyer_company || "",
            buyer_address: inv.buyer_address || "",
            ntn: inv.ntn || "N/A",
            po_number: inv.po_number || "N/A",
            po_date: inv.po_date || "",
            test_method: inv.test_method || "",
            outstanding_note: inv.outstanding_note || "",
            outstanding_pkr: inv.outstanding_pkr || 0,
            line_items: (inv.line_items || []).length
              ? inv.line_items.map((row) => ({
                  description: row.description || "",
                  qty: row.qty ?? 1,
                  rate_pkr: row.rate_pkr ?? 0,
                  total_pkr: row.total_pkr ?? 0,
                }))
              : [emptyLine()],
          });
        } else {
          setError(res.message || "Failed to load invoice.");
        }
      } else {
        setSavedId(null);
        setInvoiceNoTouched(false);
        setForm(defaultForm);
        await applySuggestedNo(defaultForm.invoice_date, defaultForm.buyer_company);
      }
      setLoading(false);
    };
    load();
  }, [editId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "document_type") {
        if (value === "quotation" && prev.payment_status === "unpaid") next.payment_status = "quotation";
        if (value === "invoice" && prev.payment_status === "quotation") next.payment_status = "unpaid";
      }
      return next;
    });
    if (name === "invoice_no") setInvoiceNoTouched(true);
  };

  // Refresh suggested number when date/company change, unless user edited Invoice No.
  useEffect(() => {
    if (editId || invoiceNoTouched || loading) return;
    const t = setTimeout(() => {
      applySuggestedNo(form.invoice_date, form.buyer_company);
    }, 250);
    return () => clearTimeout(t);
  }, [form.invoice_date, form.buyer_company]); // eslint-disable-line react-hooks/exhaustive-deps

  const regenerateInvoiceNo = async () => {
    setInvoiceNoTouched(false);
    await applySuggestedNo(form.invoice_date, form.buyer_company);
    setInvoiceNoTouched(false);
  };

  const handleLineChange = (index, field, value) => {
    setForm((prev) => {
      const line_items = prev.line_items.map((row, i) => {
        if (i !== index) return row;
        const updated = { ...row, [field]: value };
        const qty = Number(updated.qty) || 0;
        const rate = Number(updated.rate_pkr) || 0;
        if (field === "qty" || field === "rate_pkr") {
          updated.total_pkr = Math.round(qty * rate * 100) / 100;
        }
        return updated;
      });
      return { ...prev, line_items };
    });
  };

  const addLine = () => setForm((prev) => ({ ...prev, line_items: [...prev.line_items, emptyLine()] }));
  const removeLine = (index) =>
    setForm((prev) => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index),
    }));

  const subtotal = useMemo(
    () => form.line_items.reduce((sum, row) => sum + (Number(row.total_pkr) || 0), 0),
    [form.line_items]
  );
  const outstanding = Number(form.outstanding_pkr) || 0;
  const totalPayments = Math.round((subtotal + outstanding) * 100) / 100;

  const payload = () => ({
    ...form,
    outstanding_pkr: outstanding,
    line_items: form.line_items,
  });

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    const res = savedId
      ? await updateInvoiceAPI({ id: savedId, ...payload() }, token)
      : await createInvoiceAPI(payload(), token);
    setSaving(false);
    if (res.success) {
      setSavedId(res.invoice.id);
      setMessage(savedId ? "Invoice updated." : "Invoice saved to history.");
      if (!editId) navigate(`/create-invoice?id=${res.invoice.id}`, { replace: true });
    } else {
      setError(res.message || "Failed to save invoice.");
    }
  };

  const handlePrint = async () => {
    setError("");
    setMessage("");
    // Open tab synchronously so the browser does not block it after awaits.
    const printWindow = window.open("about:blank", "_blank");

    setPrinting(true);
    try {
      let id = savedId;
      if (!id) {
        const createRes = await createInvoiceAPI(payload(), token);
        if (!createRes.success) {
          printWindow?.close();
          setError(createRes.message || "Save the invoice first, then press Print.");
          return;
        }
        id = createRes.invoice.id;
        setSavedId(id);
        navigate(`/create-invoice?id=${id}`, { replace: true });
      } else {
        const saveRes = await updateInvoiceAPI({ id, ...payload() }, token);
        if (!saveRes.success) {
          printWindow?.close();
          setError(saveRes.message || "Failed to save before print.");
          return;
        }
      }

      const res = await printInvoiceAPI({ id }, token);
      if (res.success && res.invoice?.invoice_url) {
        setMessage("PDF generated.");
        if (printWindow) {
          printWindow.location.href = res.invoice.invoice_url;
        } else {
          window.location.assign(res.invoice.invoice_url);
        }
      } else {
        printWindow?.close();
        setError(res.message || "Failed to generate PDF.");
      }
    } catch (err) {
      printWindow?.close();
      setError(err.message || "Failed to generate PDF.");
    } finally {
      setPrinting(false);
    }
  };

  const textField = (name, label, extra = {}) => (
    <Form.Group className="mb-3">
      <Form.Label className="cp-form-label">{label}</Form.Label>
      <Form.Control className="cp-form-control" name={name} value={form[name]} onChange={handleChange} {...extra} />
    </Form.Group>
  );

  return (
    <DashboardLayout pageTitle={savedId ? "Edit Invoice" : "Create Invoice"}>
      <div className="cp-header">
        <div>
          <h2 className="cp-header__title">{savedId ? "Edit Invoice / Quotation" : "Create Invoice / Quotation"}</h2>
          <p className="cp-header__sub">Private billing documents — not available on public search. No QR code.</p>
        </div>
        <Link to="/all-invoices" className="cp-modal-cancel"><BsArrowLeft /> All Invoices</Link>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loading ? (
        <Spinner animation="border" />
      ) : (
        <Form onSubmit={handleSave} className="ac-form-card">
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Document type</Form.Label>
                <Form.Select
                  className="cp-form-control"
                  name="document_type"
                  value={form.document_type}
                  onChange={handleChange}
                  disabled={Boolean(editId) && !isSuperAdmin}
                >
                  <option value="invoice">Cash Invoice</option>
                  <option value="quotation">Quotation</option>
                </Form.Select>
                {editId && !isSuperAdmin && (
                  <Form.Text muted>Only super admin can change document type.</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Payment status</Form.Label>
                <Form.Select
                  className="cp-form-control"
                  name="payment_status"
                  value={form.payment_status}
                  onChange={handleChange}
                  disabled={Boolean(editId) && !isSuperAdmin}
                >
                  {PAYMENT_STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
                {editId && !isSuperAdmin && (
                  <Form.Text muted>Only super admin can change payment status.</Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Invoice No. *</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    className="cp-form-control"
                    name="invoice_no"
                    value={form.invoice_no}
                    onChange={handleChange}
                    required
                    placeholder="Invoice#184-02092026-HTMA"
                  />
                  {!editId && (
                    <button type="button" className="cp-edit-btn" title="Regenerate" onClick={regenerateInvoiceNo}>
                      Auto
                    </button>
                  )}
                </div>
                <Form.Text muted>Must be unique for every invoice. Auto from sequence + date (+ buyer); you can change it.</Form.Text>
              </Form.Group>
            </Col>
            <Col md={3}>{textField("invoice_date", "Date *", { type: "date", required: true })}</Col>
          </Row>

          <h6 className="cc-form-section">Bill To / Buyer</h6>
          <Row>
            <Col md={4}>{textField("buyer_name", "Bill To (name)")}</Col>
            <Col md={4}>{textField("buyer_company", "Buyer (M/S)")}</Col>
            <Col md={4}>{textField("buyer_address", "Address")}</Col>
            <Col md={3}>{textField("ntn", "NTN")}</Col>
            <Col md={3}>{textField("po_number", "PO#")}</Col>
            <Col md={3}>{textField("po_date", "PO Date", { type: "date" })}</Col>
            <Col md={3}>{textField("test_method", "Test Method")}</Col>
          </Row>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <h6 className="cc-form-section mb-0">Line items</h6>
            <button type="button" className="cp-edit-btn" onClick={addLine}><BsPlus size={13} /> Add row</button>
          </div>
          {form.line_items.map((row, index) => (
            <div key={index} className="cc-fm200-row">
              <div className="cc-fm200-row__head">
                <strong>Item {String(index + 1).padStart(2, "0")}</strong>
                <button type="button" className="cp-delete-btn" disabled={form.line_items.length === 1} onClick={() => removeLine(index)}>
                  <BsTrash size={13} />
                </button>
              </div>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-2">
                    <Form.Label className="cp-form-label">Description</Form.Label>
                    <Form.Control className="cp-form-control" value={row.description} onChange={(e) => handleLineChange(index, "description", e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-2">
                    <Form.Label className="cp-form-label">Qty</Form.Label>
                    <Form.Control className="cp-form-control" type="number" min="0" step="1" value={row.qty} onChange={(e) => handleLineChange(index, "qty", e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-2">
                    <Form.Label className="cp-form-label">Rate (PKR)</Form.Label>
                    <Form.Control className="cp-form-control" type="number" min="0" step="0.01" value={row.rate_pkr} onChange={(e) => handleLineChange(index, "rate_pkr", e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-2">
                    <Form.Label className="cp-form-label">Total (PKR)</Form.Label>
                    <Form.Control className="cp-form-control" value={money(row.total_pkr)} readOnly />
                  </Form.Group>
                </Col>
              </Row>
            </div>
          ))}

          <h6 className="cc-form-section mt-3">Totals</h6>
          <Row>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Subtotal (PKR)</Form.Label>
                <Form.Control className="cp-form-control" value={money(subtotal)} readOnly />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Outstanding Invoice No.</Form.Label>
                <Form.Control
                  className="cp-form-control"
                  name="outstanding_note"
                  value={form.outstanding_note}
                  onChange={handleChange}
                  placeholder="Invoice#173-02092026-HTMA"
                />
                <Form.Text muted>Prior unpaid invoice number (same format as Invoice No.).</Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Outstanding (PKR)</Form.Label>
                <Form.Control className="cp-form-control" type="number" min="0" step="0.01" name="outstanding_pkr" value={form.outstanding_pkr} onChange={handleChange} />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group className="mb-3">
                <Form.Label className="cp-form-label">Total Payments (PKR)</Form.Label>
                <Form.Control className="cp-form-control" value={money(totalPayments)} readOnly />
                <Form.Text muted>Subtotal + Outstanding</Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <div className="d-flex gap-2 flex-wrap">
            <button className="cp-modal-submit" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" animation="border" /> : <><BsSave /> Save</>}
            </button>
            <button className="cp-edit-btn" type="button" onClick={handlePrint} disabled={printing}>
              {printing ? <Spinner size="sm" animation="border" /> : <><BsPrinter /> Print PDF</>}
            </button>
          </div>
        </Form>
      )}
    </DashboardLayout>
  );
};

export default CreateInvoicePage;
